import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportWebError } from "../lib/api";
import { StatusMessage } from "./ui/StatusMessage";

type Props = { children: ReactNode };
type State = { error: Error | null; requestId: string | null };

function generateClientRequestId(): string {
  return "req_" + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, requestId: null };

  static getDerivedStateFromError(error: Error): State {
    const reqId = (error as { requestId?: string })?.requestId || generateClientRequestId();
    return { error, requestId: reqId };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("UI error:", error, info.componentStack);
    const reqId = this.state.requestId || (error as { requestId?: string })?.requestId || generateClientRequestId();
    reportWebError({
      message: error.message || "React UI crash",
      exception_type: error.name || "ReactErrorBoundary",
      stack_trace: (error.stack || "") + "\n\nComponent Stack:\n" + (info.componentStack || ""),
      request_id: reqId,
      url: typeof window !== "undefined" ? window.location.pathname : undefined,
    });
  }

  render() {
    if (this.state.error) {
      const reqId = this.state.requestId || undefined;
      return (
        <div className="flex items-center justify-center p-6 my-8">
          <div className="w-full max-w-md">
            <StatusMessage
              variant="error"
              title="Something went wrong"
              message={`Something went wrong. Please try again.${reqId ? ` Ref: ${reqId}` : ""}`}
              requestId={reqId}
              action={{
                label: "Try again",
                onClick: () => this.setState({ error: null, requestId: null }),
              }}
            />
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
